(() => {
  if (window.rqVariantPreferenceInitialized) return;
  window.rqVariantPreferenceInitialized = true;

  // Actual Shopify option names and values used in the store
  const GRADE_ORDER = ['A+ Excellent', 'A Very Good', 'B Fair', 'C Poor'];
  const TYPE_ORDER  = ['Sealed', 'Complete', 'Boxed', 'Loose'];

  const TYPE_OPTION  = 'Item Type';
  const GRADE_OPTION = 'Condition';

  const STORAGE_KEY_TYPE  = 'rq_pref_type';
  const STORAGE_KEY_GRADE = 'rq_pref_grade';
  const STORAGE_KEY_COLLECTION = 'rq_pref_collection';

  let selectedType  = sessionStorage.getItem(STORAGE_KEY_TYPE)  ?? '';
  let selectedGrade = sessionStorage.getItem(STORAGE_KEY_GRADE) ?? '';

  function getCollectionTitle() {
    const titleEl = document.querySelector('.filters-head_collection-title');
    return titleEl ? titleEl.textContent.trim() : '';
  }

  function getCollectionPreferenceDefaults(title) {
    const normalizedTitle = String(title || '').toLowerCase();
    if (!normalizedTitle) return null;

    if (normalizedTitle.includes('bundles')) {
      return { type: '', grade: 'A Very Good' };
    }

    if (
      normalizedTitle.includes('games') ||
      normalizedTitle.includes('boxed consoles')
    ) {
      return { type: 'Boxed', grade: 'A Very Good' };
    }

    if (
      normalizedTitle.includes('controllers') ||
      normalizedTitle.includes('accessories')
    ) {
      return { type: 'Loose', grade: 'A Very Good' };
    }

    return null;
  }

  function applyCollectionDefaults() {
    const collectionTitle = getCollectionTitle();
    if (!collectionTitle) return;

    const storedCollection = sessionStorage.getItem(STORAGE_KEY_COLLECTION);
    const defaults = getCollectionPreferenceDefaults(collectionTitle);
    const hasStoredCollection = storedCollection !== null;
    const shouldResetForCollection = storedCollection !== collectionTitle;
    const shouldApplyInitialDefault = !hasStoredCollection && defaults && selectedType === '' && selectedGrade === '';

    if (!shouldResetForCollection && !shouldApplyInitialDefault) return;

    selectedType = defaults ? defaults.type : '';
    selectedGrade = defaults ? defaults.grade : '';

    if (selectedType) {
      sessionStorage.setItem(STORAGE_KEY_TYPE, selectedType);
    } else {
      sessionStorage.removeItem(STORAGE_KEY_TYPE);
    }

    if (selectedGrade) {
      sessionStorage.setItem(STORAGE_KEY_GRADE, selectedGrade);
    } else {
      sessionStorage.removeItem(STORAGE_KEY_GRADE);
    }

    sessionStorage.setItem(STORAGE_KEY_COLLECTION, collectionTitle);
  }

  function formatMoney(cents) {
    const fmt = window.money_format || '£{{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function getLowestVariantPrice(variants) {
    return variants.reduce((lowest, variant) => (
      lowest === null || variant.price < lowest ? variant.price : lowest
    ), null);
  }

  function findBestVariant(variants, optionNames, preferredType, preferredGrade) {
    const typeIdx  = optionNames.indexOf(TYPE_OPTION);
    const gradeIdx = optionNames.indexOf(GRADE_OPTION);

    if (typeIdx === -1 && gradeIdx === -1) return null;

    const typeMatch  = (v) => typeIdx  === -1 || preferredType  === '' || v.options[typeIdx]  === preferredType;
    const gradeMatch = (v) => gradeIdx === -1 || preferredGrade === '' || v.options[gradeIdx] === preferredGrade;

    // 1. Exact match in stock
    let v = variants.find(v => v.available && typeMatch(v) && gradeMatch(v));
    if (v) return { variant: v, fallback: false };

    // 2. Same type, best available grade
    if (preferredType && typeIdx !== -1) {
      for (const grade of GRADE_ORDER) {
        v = variants.find(v => v.available && v.options[typeIdx] === preferredType && (gradeIdx === -1 || v.options[gradeIdx] === grade));
        if (v) return { variant: v, fallback: true };
      }
    }

    // 3. Fall back to Loose (most commonly available type), best grade first
    if (typeIdx !== -1) {
      for (const grade of GRADE_ORDER) {
        v = variants.find(v =>
          v.available &&
          v.options[typeIdx] === 'Loose' &&
          (gradeIdx === -1 || v.options[gradeIdx] === grade)
        );
        if (v) return { variant: v, fallback: true };
      }
    }

    // 4. Any available variant (best type + grade combo)
    for (const type of TYPE_ORDER) {
      for (const grade of GRADE_ORDER) {
        v = variants.find(v =>
          v.available &&
          (typeIdx  === -1 || v.options[typeIdx]  === type) &&
          (gradeIdx === -1 || v.options[gradeIdx] === grade)
        );
        if (v) return { variant: v, fallback: true };
      }
    }

    // 5. Exact OOS match (show price anyway)
    v = variants.find(v => typeMatch(v) && gradeMatch(v));
    if (v) return { variant: v, fallback: true };

    // 6. Best OOS variant by priority
    for (const type of TYPE_ORDER) {
      for (const grade of GRADE_ORDER) {
        v = variants.find(v =>
          (typeIdx  === -1 || v.options[typeIdx]  === type) &&
          (gradeIdx === -1 || v.options[gradeIdx] === grade)
        );
        if (v) return { variant: v, fallback: true };
      }
    }

    return null;
  }

  function updateCard(card) {
    if (card.dataset.rqBundleShell === 'true') return;

    const scriptEl = card.querySelector('[type="application/json"]');
    if (!scriptEl) return;

    let product;
    try { product = JSON.parse(scriptEl.textContent); } catch (e) { return; }

    const { variants, options } = product;
    if (!variants || !options) return;

    const result = findBestVariant(variants, options, selectedType, selectedGrade);
    if (!result) return;

    const { variant, fallback } = result;
    const typeIdx  = options.indexOf(TYPE_OPTION);
    const gradeIdx = options.indexOf(GRADE_OPTION);
    const anyInStock = variants.some(v => v.available);
    const hasPreference = selectedType !== '' || selectedGrade !== '';

    // Update price — only write if changed to avoid MutationObserver loop
    const priceEl = card.querySelector('.main-price');
    if (priceEl) {
      const lowestPrice = getLowestVariantPrice(variants);
      const shouldShowFromPrice = !anyInStock && lowestPrice !== null;
      const newPrice = shouldShowFromPrice ? `From ${formatMoney(lowestPrice)}` : formatMoney(variant.price);
      if (priceEl.textContent !== newPrice) priceEl.textContent = newPrice;
    }

    // Update variant label (shows Item Type + Condition, skips Version)
    const labelEl = card.querySelector('[data-variant-preference-label]');
    if (labelEl) {
      if (!anyInStock) {
        const outOfStockLabel = labelEl.dataset.outOfStockLabel || 'Sold out';
        if (labelEl.textContent !== outOfStockLabel) labelEl.textContent = outOfStockLabel;
        labelEl.classList.remove('is-fallback');
        labelEl.classList.add('is-out-of-stock');
      } else {
        const parts = [];
        if (typeIdx  !== -1) parts.push(variant.options[typeIdx]);
        if (gradeIdx !== -1) parts.push(variant.options[gradeIdx]);

        const isFallback = hasPreference && fallback;
        const variantLabel = parts.join(' / ').replace('Complete', 'CIB');

        if (labelEl.textContent !== variantLabel) labelEl.textContent = variantLabel;
        labelEl.removeAttribute('data-tooltip');
        labelEl.classList.toggle('is-fallback', isFallback);
        labelEl.classList.remove('is-out-of-stock');
      }
    }
  }

  function updateAllCards() {
    document.querySelectorAll('product-card').forEach(updateCard);
  }

  function restoreSelectValues() {
    document.querySelectorAll('.vp-pref-item').forEach(item => {
      const pref  = item.dataset.preference;
      const value = item.dataset.value;
      item.classList.toggle('active',
        (pref === 'type'  && value === selectedType) ||
        (pref === 'grade' && value === selectedGrade)
      );
    });

    const typeActive  = document.querySelector('.vp-pref-item.active[data-preference="type"]');
    const gradeActive = document.querySelector('.vp-pref-item.active[data-preference="grade"]');
    const typeButtons = document.querySelectorAll('[data-preference-trigger="type"], #vp-btn-type');
    const gradeButtons = document.querySelectorAll('[data-preference-trigger="grade"], #vp-btn-grade');

    typeButtons.forEach((typeBtn) => {
      if (typeActive) typeBtn.querySelector('span').textContent = typeActive.textContent.trim() || typeActive.dataset.value;
    });

    gradeButtons.forEach((gradeBtn) => {
      if (gradeActive) gradeBtn.querySelector('span').textContent = gradeActive.textContent.trim() || gradeActive.dataset.value;
    });
  }

  const REGION_REPLACEMENTS = [
    { pattern: /\(EU\)/g, emoji: '🇪🇺' },
    { pattern: /\(JP\)/g, emoji: '🇯🇵' },
    { pattern: /\(NA\)/g, emoji: '🇺🇸' },
    { pattern: /\(GL\)/g, emoji: '🌍' },
  ];
  const GLOBAL_REGION_LABEL_PATTERN = /(?:\s*🌍\s*)?\bGlobal\b(?:\s*🌍)?/g;

  function replaceRegionLabels() {
    document.querySelectorAll('.filter-widget_list .custom-form-label').forEach(label => {
      for (const node of label.childNodes) {
        if (node.nodeType !== Node.TEXT_NODE) continue;
        let text = node.textContent;
        REGION_REPLACEMENTS.forEach(({ pattern, emoji }) => { text = text.replace(pattern, emoji); });
        text = text.replace(GLOBAL_REGION_LABEL_PATTERN, 'Global 🌍');
        if (text !== node.textContent) node.textContent = text;
      }
    });

    // Rename "Region code" title to "Region"
    document.querySelectorAll('.filter-widget_title').forEach(el => {
      if (el.textContent.trim() === 'Region code') el.textContent = 'Region';
    });
  }

  function init() {
    // Clear stale sessionStorage values that used old option format (single letters)
    if (selectedGrade && !GRADE_ORDER.includes(selectedGrade)) {
      selectedGrade = '';
      sessionStorage.removeItem(STORAGE_KEY_GRADE);
    }

    applyCollectionDefaults();
    restoreSelectValues();
    replaceRegionLabels();

    // Use event delegation on document so re-renders can't break the binding
    document.addEventListener('click', (e) => {
      const item = e.target.closest('.vp-pref-item');
      if (!item) return;
      e.preventDefault();

      const pref  = item.dataset.preference;
      const value = item.dataset.value;

      if (pref === 'type') {
        selectedType = value;
        sessionStorage.setItem(STORAGE_KEY_TYPE, selectedType);
      } else if (pref === 'grade') {
        selectedGrade = value;
        sessionStorage.setItem(STORAGE_KEY_GRADE, selectedGrade);
      }
      const collectionTitle = getCollectionTitle();
      if (collectionTitle) sessionStorage.setItem(STORAGE_KEY_COLLECTION, collectionTitle);

      // Update trigger label and close dropdown
      const dropdown = item.closest('dropdown-component');
      if (dropdown) {
        const triggerSpan = dropdown.querySelector('.dropdown-component_opener span');
        if (triggerSpan) triggerSpan.textContent = item.textContent.trim() || value;
        if (typeof dropdown.close === 'function') dropdown.close();
      }
      restoreSelectValues();

      updateAllCards();
    });

    updateAllCards();

    // Re-apply after filter/sort AJAX updates (only when new card elements are added)
    const grid = document.getElementById('filters-grid');
    if (grid) {
      new MutationObserver((mutations) => {
        const hasNewElements = mutations.some(m =>
          Array.from(m.addedNodes).some(n => n.nodeType === 1)
        );
        if (hasNewElements) updateAllCards();
      }).observe(grid, { childList: true, subtree: true });
    }

    // After AJAX re-renders filters-head: restore select values + re-move pills
    const filtersHead = document.querySelector('.filters-head');
    if (filtersHead) {
      new MutationObserver(() => {
        restoreSelectValues();
        updateAllCards();
      }).observe(filtersHead, { childList: true });
    }

    // Re-apply region label replacements after sidebar filter AJAX updates
    const sidebarForm = document.querySelector('filters-form[data-preserver="true"]');
    if (sidebarForm) {
      new MutationObserver((mutations) => {
        const hasNewElements = mutations.some(m =>
          Array.from(m.addedNodes).some(n => n.nodeType === 1)
        );
        if (hasNewElements) replaceRegionLabels();
      }).observe(sidebarForm, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
